import { Address, MarketMeta, makeUsd } from "@silo/types";
import { Array, BigInt, Effect, Option, pipe } from "effect";
import { getRawDashboardMarketAsset } from "./get-raw-dashboard-market-asset";
import { RawDashboardMarket } from "./types";
import { getConfigIsDeprecated } from "@silo/config";
import {
  calculateLtv,
  getBorrowingPowerUsed,
  getIsAtRiskOfLiquidation,
  mapToDebtLtvAsset,
  mapToDepositLtvAsset,
} from "./ltv-helpers";

export type GetRawDashboardMarket = ReturnType<typeof getRawDashboardMarket>;
export type GetRawDashboardMarketSuccess =
  Effect.Effect.Success<GetRawDashboardMarket>;
export type GetRawDashboardMarketError =
  Effect.Effect.Error<GetRawDashboardMarket>;
export type GetRawDashboardMarketContext =
  Effect.Effect.Context<GetRawDashboardMarket>;

interface GetRawDashboardMarketParams {
  user: Address;
  marketMeta: MarketMeta;
}

export function getRawDashboardMarket({
  user,
  marketMeta,
}: GetRawDashboardMarketParams) {
  return Effect.gen(function* () {
    const { marketAddress, protocolKey } = marketMeta;

    const isDeprecated =
      marketMeta.isDeprecated ||
      getConfigIsDeprecated({ protocolKey, marketAddress });

    const marketAssetMetaArray = pipe(
      [marketMeta.baseAsset, ...marketMeta.bridgeAssets],
      Array.flatMapNullable((asset) => asset)
    );

    const [baseAsset, firstBridgeAsset, secondBridgeAsset] = yield* Effect.all(
      [
        getRawDashboardMarketAsset({
          marketAssetMeta: marketMeta.baseAsset,
          user,
        }),
        getRawDashboardMarketAsset({
          marketAssetMeta: marketMeta.bridgeAssets[0],
          user,
        }),
        pipe(
          marketMeta.bridgeAssets[1],
          Option.fromNullable,
          Option.match({
            onNone: () => Effect.succeed(undefined),
            onSome: (marketAssetMeta) =>
              getRawDashboardMarketAsset({
                marketAssetMeta,
                user,
              }),
          })
        ),
      ],
      { concurrency: "unbounded" }
    );

    const rawDashboardMarketAssets = pipe(
      [baseAsset, firstBridgeAsset, secondBridgeAsset],
      Array.flatMapNullable((asset) => asset)
    );

    const depositLtvAssets = pipe(
      rawDashboardMarketAssets,
      Array.filter(
        (asset) => asset.collateralBalance > 0n || asset.protectedBalance > 0n
      ),
      Array.map(mapToDepositLtvAsset)
    );

    const debtLtvAssets = pipe(
      rawDashboardMarketAssets,
      Array.filter((asset) => asset.debtBalance > 0n),
      Array.map(mapToDebtLtvAsset)
    );

    const ltv = calculateLtv({
      depositAssets: depositLtvAssets,
      debtAssets: debtLtvAssets,
    });

    const borrowingPowerUsed = getBorrowingPowerUsed(
      ltv.userLtv,
      ltv.userMaxLtv
    );

    const isAtRiskOfLiquidation = getIsAtRiskOfLiquidation(
      ltv.healthFactor,
      marketAssetMetaArray.map((asset) => asset.lt)
    );

    const depositBalanceUsd = pipe(
      rawDashboardMarketAssets,
      Array.map((asset) => asset.depositBalanceUsd),
      BigInt.sumAll,
      makeUsd
    );

    const debtBalanceUsd = pipe(
      rawDashboardMarketAssets,
      Array.map((asset) => asset.debtBalanceUsd),
      BigInt.sumAll,
      makeUsd
    );

    const dashboardMarket: RawDashboardMarket = {
      marketAddress,
      marketSymbol: baseAsset.symbol,
      isDeprecated,

      userLtv: ltv.userLtv,
      userLiquidationThreshold: ltv.userLiquidationThreshold,
      healthFactor: ltv.healthFactor,
      borrowingPowerUsed,
      isAtRiskOfLiquidation,

      depositBalanceUsd,
      debtBalanceUsd,

      baseAsset,
      bridgeAssets: secondBridgeAsset
        ? [firstBridgeAsset, secondBridgeAsset]
        : [firstBridgeAsset],
    };

    return dashboardMarket;
  });
}
